import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface OrderCreationContextType {
    isCreatingOrder: boolean;
    orderCategory: string | null;
    orderSubcategory: string | null;
    setOrderDetails: (category: string | null, subcategory: string | null) => void;
    clearOrderCreation: () => void;
}

const OrderCreationContext = createContext<OrderCreationContextType | undefined>(undefined);

interface OrderCreationProviderProps {
    children: ReactNode;
}

export function OrderCreationProvider({ children }: OrderCreationProviderProps) {
    const [isCreatingOrder, setIsCreatingOrder] = useState(false);
    const [orderCategory, setOrderCategory] = useState<string | null>(null);
    const [orderSubcategory, setOrderSubcategory] = useState<string | null>(null);

    const setOrderDetails = useCallback((category: string | null, subcategory: string | null) => {
        setOrderCategory(category);
        setOrderSubcategory(subcategory);
        // Automatically set isCreatingOrder when both category and subcategory are set
        if (category && subcategory) {
            setIsCreatingOrder(true);
        }
    }, []);

    const clearOrderCreation = useCallback(() => {
        setIsCreatingOrder(false);
        setOrderCategory(null);
        setOrderSubcategory(null);
    }, []);

    return (
        <OrderCreationContext.Provider
            value={{
                isCreatingOrder,
                orderCategory,
                orderSubcategory,
                setOrderDetails,
                clearOrderCreation,
            }}
        >
            {children}
        </OrderCreationContext.Provider>
    );
}

export function useOrderCreation() {
    const context = useContext(OrderCreationContext);
    if (context === undefined) {
        throw new Error('useOrderCreation must be used within an OrderCreationProvider');
    }
    return context;
}
